import * as React from 'react';
import { hot } from 'react-hot-loader/root';
import { BrowserRouter, Route, Switch, Redirect } from 'react-router-dom';

import { FirestoreProvider } from 'client/components/react-firestore';
import firebase from 'client/utils/firebase';

import Loadable from 'react-loadable';

import { UserProvider } from 'client/components/UserContext';
import { NotificationsProvider } from 'client/components/NotificationsContext';
import { WebPushProvider } from 'client/components/WebPushContext';
import ServiceWorkerProvider from 'client/components/ServiceWorkerContext/ServiceWorkerProvider';

import NotificationsTitleUpdater from 'client/components/NotificationsTitleUpdater';

import Tracker from 'client/components/Tracker';
import ErrorHandlerUserSetter from 'client/components/ErrorHandlerUserSetter';

import GlobalStyle from 'client/styles/GlobalStyle';
import { MutableThemeProvider } from 'client/components/MutableThemeContext';

import PrivateRoute from 'client/components/PrivateRoute';
import AdminRoute from 'client/components/AdminRoute';
import ScrollToTop from 'client/components/ScrollToTop';
import ErrorBoundary from 'client/components/ErrorBoundary';

import { AppBodyContainer, PageContainer, PageFullWidthContainer, PageLoadingIndicator } from './page';
import AppHeader from './AppHeader';
import AppFooter from './AppFooter';

import Main from './Main';

const SignInModalRoot = Loadable({
  loader: () => import('client/components/signin/SignInModalRoot'),
  loading: () => <></>,
});

const Contact = Loadable({
  loader: () => import('./general/contact'),
  loading: PageLoadingIndicator,
});
const SubmitEvent = Loadable({
  loader: () => import('./general/SubmitEvent'),
  loading: PageLoadingIndicator,
});

const ArticleHowItWorks = Loadable({
  loader: () => import('./articles/ArticleHowItWorks'),
  loading: PageLoadingIndicator,
});
const ArticleSafety = Loadable({
  loader: () => import('./articles/ArticleSafety'),
  loading: PageLoadingIndicator,
});
const ArticleTermsOfService = Loadable({
  loader: () => import('./articles/ArticleTermsOfService'),
  loading: PageLoadingIndicator,
});
const ArticleCookiePolicy = Loadable({
  loader: () => import('./articles/ArticleCookiePolicy'),
  loading: PageLoadingIndicator,
});
const ArticlePrivacyPolicy = Loadable({
  loader: () => import('./articles/ArticlePrivacyPolicy'),
  loading: PageLoadingIndicator,
});
const ArticleEventOrganizers = Loadable({
  loader: () => import('./articles/ArticleEventOrganizers'),
  loading: PageLoadingIndicator,
});
const ArticleSponsors = Loadable({
  loader: () => import('./articles/ArticleSponsors'),
  loading: PageLoadingIndicator,
});
const ArticleCommunityGuidelines = Loadable({
  loader: () => import('./articles/ArticleCommunityGuidelines'),
  loading: PageLoadingIndicator,
});
const ArticleCommunities = Loadable({
  loader: () => import('./articles/ArticleCommunities'),
  loading: PageLoadingIndicator,
});

const EventContainer = Loadable({
  loader: () => import('./events/EventContainer'),
  loading: PageLoadingIndicator,
});
const Events = Loadable({
  loader: () => import('./events/Events'),
  loading: PageLoadingIndicator,
});
const EventCreate = Loadable({
  loader: () => import('./events/EventCreate'),
  loading: PageLoadingIndicator,
});

const Profile = Loadable({
  loader: () => import('./Profile'),
  loading: PageLoadingIndicator,
});
const ProfilePublic = Loadable({
  loader: () => import('./Profile/ProfilePublic'),
  loading: PageLoadingIndicator,
});

const Dashboard = Loadable({
  loader: () => import('./Dashboard'),
  loading: PageLoadingIndicator,
});
const Organize = Loadable({
  loader: () => import('./Organize'),
  loading: PageLoadingIndicator,
});

const RecentActivity = Loadable({
  loader: () => import('./personal/RecentActivity'),
  loading: PageLoadingIndicator,
});

const SignIn = Loadable({
  loader: () => import('./SignIn'),
  loading: PageLoadingIndicator,
});

const Blog = Loadable({
  loader: () => import('./Blog'),
  loading: PageLoadingIndicator,
});
const AdminConsole = Loadable({
  loader: () => import('./AdminConsole'),
  loading: PageLoadingIndicator,
});

const Pricing = Loadable({
  loader: () => import('./Pricing'),
  loading: PageLoadingIndicator,
});
const Plans = Loadable({
  loader: () => import('./Plans'),
  loading: PageLoadingIndicator,
});

const CommunityContainer = Loadable({
  loader: () => import('./communities/CommunityContainer'),
  loading: PageLoadingIndicator,
});
const CommunityCreate = Loadable({
  loader: () => import('./communities/CommunityCreate'),
  loading: PageLoadingIndicator,
});

// const Meetup = Loadable({
//   loader: () => import('./Meetup'),
//   loading: PageLoadingIndicator,
// });

const NotFound = () => <div>NotFound</div>;

import('react-modal').then(({ default: ReactModal }) => ReactModal.setAppElement('#app-render-root'));

const App = () => (
  <ServiceWorkerProvider>
    <UserProvider>
      <ErrorHandlerUserSetter />
      <FirestoreProvider firebase={firebase.app()}>
        <MutableThemeProvider>
          <>
            <GlobalStyle />
            <WebPushProvider>
              <NotificationsProvider>
                <BrowserRouter>
                  <ScrollToTop>
                    <NotificationsTitleUpdater />
                    <Tracker />
                    <SignInModalRoot />
                    <AppHeader />
                    <ErrorBoundary>
                      <Switch>
                        <Route
                          exact
                          path="/wise24"
                          render={() => <Redirect to="/events/wise24-women's-startup-lab-is6FaKFwb2ZAXrs69" />}
                        />
                        <Route exact path="/">
                          <AppBodyContainer>
                            <Main />
                          </AppBodyContainer>
                        </Route>
                        <Redirect from="/main" to="/" exact />
                        {/* <PrivateRoute
                          path="/events/create"
                          render={props => (
                            <PageContainer>
                              <EventCreate {...props} />
                            </PageContainer>
                          )}
                        /> */}
                        <Redirect from="/events/create" to="/contact" />
                        <Route
                          exact
                          path="/events"
                          render={props => (
                            <PageFullWidthContainer>
                              <Events {...props} />
                            </PageFullWidthContainer>
                          )}
                        />
                        <Route path="/events/:eventSlugId">
                          <PageFullWidthContainer>
                            <EventContainer />
                          </PageFullWidthContainer>
                        </Route>
                        <Route path="/event-organizers" component={ArticleEventOrganizers} />
                        <Route path="/event-sponsors" component={ArticleSponsors} />
                        <Route path="/communities-intro" component={ArticleCommunities} />
                        {/* <Route path="/pricing" component={Pricing} /> */}
                        <Redirect from="/pricing" to="/contact" />

                        <Route path="/community-guidelines" component={ArticleCommunityGuidelines} />
                        <Route path="/terms-of-service" component={ArticleTermsOfService} />
                        <Route path="/privacy-policy" component={ArticlePrivacyPolicy} />
                        <Route path="/cookie-policy" component={ArticleCookiePolicy} />

                        <Route path="/blog" component={Blog} />

                        {/* <Route path="/meetup" component={Meetup} /> */}

                        <Route
                          render={() => (
                            <PageContainer>
                              <Switch>
                                {/* <PrivateRoute path="/communities/create" component={CommunityCreate} /> */}
                                <Redirect from="/communities/create" to="/contact" />
                                <Route
                                  path="/communities/:communitySlugId"
                                  render={({ match }) => <CommunityContainer slug={match.params.communitySlugId} />}
                                />
                                <AdminRoute path="/admin" component={AdminConsole} />
                                <Route path="/contact" component={Contact} />
                                <Route path="/submit-event" component={SubmitEvent} />
                                <Route path="/how-it-works" component={ArticleHowItWorks} />
                                <Route path="/safety" component={ArticleSafety} />
                                <PrivateRoute path="/plans" component={Plans} />
                                <PrivateRoute
                                  path="/users/:userId"
                                  render={({ match }) => <ProfilePublic userId={match.params.userId} />}
                                />
                                <PrivateRoute path="/profile" component={Profile} />
                                <Route path="/signin" component={SignIn} />
                                {/* <PrivateRoute path="/organize" component={Organize} /> */}
                                <Redirect from="/organize" to="/contact" />
                                <PrivateRoute path="/dashboard" component={Dashboard} />
                                <PrivateRoute path="/recent-activity" component={RecentActivity} />
                                <Redirect from="/explore" to="/" />
                                <Route component={NotFound} />
                              </Switch>
                            </PageContainer>
                          )}
                        />
                      </Switch>
                    </ErrorBoundary>
                    <AppFooter />
                  </ScrollToTop>
                </BrowserRouter>
              </NotificationsProvider>
            </WebPushProvider>
          </>
        </MutableThemeProvider>
      </FirestoreProvider>
    </UserProvider>
  </ServiceWorkerProvider>
);

export default hot(App);
